import { reactive } from 'vue';

// Every chrome action (src/iframe/legacyNavActions.js) now decides its own confirmations
// (composables/useConfirmAction.js + NavConfirmDialog.vue) and calls the legacy business module
// directly instead of a view-model method that would raise a swal - none of them is expected to
// trigger one any more. `call()` still wraps every action through this adapter regardless, as a
// uniform safety net: the support actions genuinely need it (waSupport.loadScript executes an
// arbitrary remote script, and the backup handlers touch real sync queues - either could still
// raise a swal/toast on its own). Those handlers ask
// and report through sweetalert v1 (`swal`) and Toastify (`toast`), both globals of the legacy
// window. While such an action runs — and for a grace period covering its timers and XHR callbacks —
// those globals are swapped for adapters, so the dialog renders in the Vue chrome
// (NavLegacyDialogs.vue) while the legacy callback keeps the business effect.

export const legacyDialogs = reactive({ current: null, toasts: [] });

const TOAST_DURATION_MS = 5000;
// Enough for the setTimeout(…, 150 | 500) follow-ups of the legacy handlers.
export const DEFAULT_HOLD_MS = 1500;

let dialogSeq = 0;
const callbacks = new Map();
let dialogTimer = null;

let installed = null;
// Bumped on every install: a hold only releases the installation it was taken on.
let generation = 0;
let depth = 0;
let holdUntil = 0;
// Grace period of the action that raised the dialog on screen, reused when the user answers it.
let activeHoldMs = DEFAULT_HOLD_MS;
let releaseTimer = null;

const now = () => Date.now();

// sweetalert renders `text` as HTML with `html: true`: keep the line breaks, drop the markup.
export function toPlainText(value, isHtml = false) {
	const text = value == null ? '' : String(value);
	if (!isHtml) return text;
	return text
		.replace(/<br\s*\/?>/gi, '\n')
		.replace(/<\/(p|div|li)>/gi, '\n')
		.replace(/<[^>]*>/g, '')
		.replace(/&nbsp;/g, ' ')
		.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.trim();
}

// swal(title, text?, type?) or swal(options, callback?), sweetalert v1 defaults.
export function normalizeSwal(args) {
	const [first, second, third] = args;
	const options = first && typeof first === 'object' ? first : { title: first ?? '', text: second ?? '', type: third ?? null };
	const callback = first && typeof first === 'object' && typeof second === 'function' ? second : null;
	const type = options.type ?? null;

	return {
		dialog: {
			title: toPlainText(options.title, options.html),
			text: toPlainText(options.text, options.html),
			type: type === 'input' ? 'input' : ['success', 'error', 'warning', 'info'].includes(type) ? type : null,
			imageUrl: options.imageUrl ?? null,
			inputType: options.inputType ?? 'text',
			inputPlaceholder: options.inputPlaceholder ?? '',
			inputValue: options.inputValue ?? '',
			inputError: null,
			showCancelButton: Boolean(options.showCancelButton),
			showConfirmButton: options.showConfirmButton !== false,
			confirmText: options.confirmButtonText ?? 'OK',
			cancelText: options.cancelButtonText ?? 'Cancel',
			closeOnConfirm: options.closeOnConfirm !== false,
			closeOnCancel: options.closeOnCancel !== false,
			allowEscapeKey: options.allowEscapeKey !== false,
			allowOutsideClick: Boolean(options.allowOutsideClick),
			timer: Number(options.timer) > 0 ? Number(options.timer) : null,
		},
		callback,
	};
}

// sweetalert v1 only reports a cancel to callbacks written `function (arg) {…}` (handleCancel()).
export function callbackHandlesCancel(callback) {
	if (typeof callback !== 'function') return false;
	const source = String(callback).replace(/\s/g, '');
	return source.substring(0, 9) === 'function(' && source.substring(9, 10) !== ')';
}

function closeCurrent() {
	clearTimeout(dialogTimer);
	dialogTimer = null;
	if (legacyDialogs.current) callbacks.delete(legacyDialogs.current.id);
	legacyDialogs.current = null;
}

// The callback runs the legacy business effect: it may chain another dialog, so it runs captured too.
function invoke(entry, value) {
	if (typeof entry?.callback !== 'function') return;
	runWithVueDialogs(installed?.win ?? entry.win, () => entry.callback(value), { holdMs: entry.holdMs });
}

function openDialog(args) {
	const { dialog, callback } = normalizeSwal(args);
	// Like sweetalert, a new call replaces the dialog on screen.
	closeCurrent();
	const id = ++dialogSeq;
	const entry = { callback, holdMs: activeHoldMs, win: installed?.win ?? null };
	callbacks.set(id, entry);
	legacyDialogs.current = { id, ...dialog };

	if (dialog.timer) {
		dialogTimer = setTimeout(() => {
			if (legacyDialogs.current?.id !== id) return;
			closeCurrent();
			invoke(entry, null);
		}, dialog.timer);
	}
}

export function confirmLegacyDialog(value) {
	const current = legacyDialogs.current;
	if (!current) return;
	const entry = callbacks.get(current.id);
	if (current.closeOnConfirm) closeCurrent();
	invoke(entry, current.type === 'input' ? (value ?? '') : true);
}

export function cancelLegacyDialog() {
	const current = legacyDialogs.current;
	if (!current) return;
	const entry = callbacks.get(current.id);
	if (current.closeOnCancel) closeCurrent();
	if (callbackHandlesCancel(entry?.callback)) invoke(entry, false);
}

function createSwalAdapter(original) {
	const swal = (...args) => openDialog(args);
	swal.close = closeCurrent;
	swal.showInputError = (message) => {
		if (legacyDialogs.current) legacyDialogs.current.inputError = toPlainText(message, true);
	};
	swal.resetInputError = () => {
		if (legacyDialogs.current) legacyDialogs.current.inputError = null;
	};
	swal.enableButtons = () => {};
	swal.disableButtons = () => {};
	swal.setDefaults = (...args) => original?.setDefaults?.(...args);
	return swal;
}

// toast(message, type = 'success', isHtml = false, …) from js/Main.js; consumed by a VSnackbarQueue.
export function pushLegacyToast(message, type = 'success', isHtml = false) {
	legacyDialogs.toasts.push({ text: toPlainText(message, isHtml === true), color: ['success', 'warning', 'info'].includes(type) ? type : 'error', timeout: TOAST_DURATION_MS });
}

function install(win) {
	if (installed?.win === win) return;
	release();
	try {
		generation++;
		installed = { win, swal: win.swal, sweetAlert: win.sweetAlert, toast: win.toast };
		const swal = createSwalAdapter(installed.swal);
		win.swal = swal;
		win.sweetAlert = swal;
		win.toast = pushLegacyToast;
	} catch {
		installed = null;
	}
}

function release() {
	clearTimeout(releaseTimer);
	releaseTimer = null;
	if (!installed) return;
	const { win, swal, sweetAlert, toast } = installed;
	installed = null;
	try {
		win.swal = swal;
		win.sweetAlert = sweetAlert;
		win.toast = toast;
	} catch {
		// the legacy document is gone
	}
}

function scheduleRelease() {
	if (depth > 0) return;
	clearTimeout(releaseTimer);
	const delay = holdUntil - now();
	if (delay <= 0) release();
	else releaseTimer = setTimeout(scheduleRelease, delay);
}

/**
 * Runs `fn` with the legacy window's `swal` / `toast` rendered by the Vue chrome, for the call and
 * `holdMs` after it (timers and XHR callbacks of the legacy handler). Overlapping calls extend the hold.
 */
export function runWithVueDialogs(win, fn, { holdMs = DEFAULT_HOLD_MS } = {}) {
	if (!win) return fn();
	install(win);
	holdUntil = Math.max(holdUntil, now() + holdMs);
	const previousHoldMs = activeHoldMs;
	activeHoldMs = depth > 0 ? Math.max(activeHoldMs, holdMs) : holdMs;
	depth++;
	try {
		return fn();
	} finally {
		depth--;
		activeHoldMs = previousHoldMs;
		scheduleRelease();
	}
}

/**
 * Keeps the adapters installed until the returned release() is called: while a Vue page covers a legacy
 * page that still runs (login, portal), a dialog its XHR callbacks or timers raise outside any chrome
 * action would otherwise open in the hidden iframe.
 */
export function holdVueDialogs(win) {
	if (!win) return () => {};
	install(win);
	if (installed?.win !== win) return () => {};
	depth++;
	const heldOn = generation;
	let held = true;
	return () => {
		if (!held) return;
		held = false;
		if (!installed || generation !== heldOn) return;
		depth = Math.max(0, depth - 1);
		scheduleRelease();
	};
}

// iframe reload: the adapters belong to the old document.
export function resetLegacyDialogs() {
	depth = 0;
	holdUntil = 0;
	activeHoldMs = DEFAULT_HOLD_MS;
	release();
	closeCurrent();
	callbacks.clear();
}
